<script setup>

import { useRouter, useRoute } from "vue-router"
import {computed, onMounted, onUnmounted, reactive, ref, watch} from "vue";
import { useToast } from "vue-toastification";
import { Vue3Lottie as Lottie } from 'vue3-lottie'
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { default as equipmentApi } from "@/Api/equipment-api";
import { illustrationsApi} from "@/Api/illustrations-api";
import { npcApi } from "@/Api/npc-api";
import { useUserStore } from "@/Stores/userStore";
import { useProgressStore } from "@/Stores/progressStore";
import { useStatusStore } from "@/Stores/statusStore";
import { usePromptStore } from "@/Stores/promptStore";
import EquipmentList from "@/Components/EquipmentList.vue";
import EquipmentPreview from "@/Components/EquipmentPreview.vue";
import LoadingMotion from '@/images/animations/loading-motion.json'
import ChemistryFlask from '@/images/animations/chemistry-flask.json'
import EquipmentStashGenerateForm from "@/Components/GenerationForms/EquipmentStashGenerateForm.vue";
import PrimaryLayout from "@/Layouts/PrimaryLayout.vue";
import MerchantInfo from "@/Components/Npcs/MerchantInfo.vue";
import NpcGenerateForm from "@/Components/GenerationForms/NpcGenerateForm.vue";
import MiniPreview from "@/Components/MiniPreview.vue";
import _ from 'lodash';
import LoginModal from "@/Modals/LoginModal.vue";
import RegistrationModal from "@/Modals/RegistrationModal.vue";
import {initializeAbly} from "@/Api/api";
import NpcList from "@/Pages/Compendium/NpcList.vue";
import BookletContentForm from "@/Components/GenerationForms/BookletContentForm.vue";
import MetadataPreview from "@/Components/Metadata/MetadataPreview.vue";
import OptionsMenu from "@/Components/OptionsMenu.vue";
import SelectStyleForm from "@/Components/GenerationForms/SelectStyleForm.vue";
import ModifyContentForm from "@/Components/GenerationForms/ModifyContentForm.vue";
import GenerateActionPanel from "@/Components/ActionPanels/GenerateActionPanel.vue";
import {useCreditStore} from "@/Stores/creditStore";
import CreditsExhaustedModal from "@/Modals/CreditsExhaustedModal.vue";
import SearchIllustrationsModal from "@/Modals/SearchIllustrationsModal.vue";
import clarityService from "@/Services/clarity";
import {useGenerationTaskStore} from "@/Stores/generationTaskStore";
import SubscriptionPricingModal from "@/Components/Subscriptions/SubscriptionPricingModal.vue";
import NpcSkeletonCard from "@/Components/Npcs/NpcSkeletonCard.vue";
import ReturnToListButton from "@/Components/Navigation/ReturnToListButton.vue";

const router = useRouter();
const route = useRoute();
const toast = useToast();
const userStore = useUserStore()
const progressStore = useProgressStore();
const statusStore = useStatusStore();
const promptStore = usePromptStore();
const creditStore = useCreditStore();
const generationStore = useGenerationTaskStore();

const activeGenerations = ref([]);
const skeletonRefs = ref({});
let unsubscribeTaskCompleted = null;
const isGenerating = computed(() => activeGenerations.value.length > 0);
const showBackButton = computed(() => !!route.query.npc_id && !!data.response.npc);

function removeActiveGeneration(taskId) {
    activeGenerations.value = activeGenerations.value.filter(g => g.taskId !== taskId);
}

const emits = defineEmits(['login-required'])

const data = reactive({
    request: {
        influence: "",
    },
    response: {
        npc: null,
        equipment: null,
        stash_id: null,
        historyBooklet: null,
    },
    queryNpcId: null,
    selectedImage: null,
    selectedImageType: null,
    selectedItem: null,
    displayRegenerationLimit: null,
    shouldDisplayEquipmentGenerateForm: false,
    shouldDisplayAddEquipmentForm: false,
    shouldDisplayNpcGenerateForm: false,
    shouldDisplayMetadataPreview: false,
    shouldDisplaySelectStyleForm: false,
    shouldDisplayModifyContentForm: false,
    shouldDisplaySearchIllustrationModal: false,
    shouldDisplayCreditPlans: false,
    showConcurrencyPricingModal: false,
    isLoading: true,
    authenticationCallback: null,
    isEditing: false,
})

let currentNpcChannel = null;
let currentEquipmentChannel = null;

function requestMerchant(npcId) {
    data.isLoading = true;
    const request = { npc_id: npcId }
    npcApi.getNpcById(request).then(response => {
        data.isLoading = false;
        data.response.npc = response.data;
        data.response.stash_id = response.data.stash_id;
        if (data.response.stash_id) {
            requestEquipment(data.response.stash_id)
        }
        subscribeToEvents();
    }).catch(function (error) {
        data.isLoading = false;
        data.response.equipment = null
        const message = error.response?.data?.message ?? "Something went wrong. Please try again.";
        toast.error(message);
    });
}

function generateMerchant(request) {
    if (!userStore.isLoggedIn()) {
        data.displayRegister = true;
        data.authenticationCallback = () => {
            data.displayLogin = false;
            data.displayRegister = false;
        }
        return true
    }

    if (!generationStore.canGenerate) {
        data.showConcurrencyPricingModal = true;
        return;
    }

    const localGenId = Date.now().toString();

    function afterEstimateConfirmation() {
        data.isLoading = false;
        data.response.equipment = null
        data.response.npc = null
        data.shouldDisplayNpcGenerateForm = false
        statusStore.resetLapsedTime();
        window.scrollTo(0, 0)
        router.replace({ path: '/merchant' })

        activeGenerations.value.unshift({
            taskId: localGenId,
            influence: request.influence || '',
            edition: request.edition || '5e',
        });

        if (!data.response.historyBooklet) {
            requestNpcHistory();
        }

        setTimeout(() => {
            skeletonRefs.value[localGenId]?.startProgress();
        }, 50);
    }

    npcApi.generateNpc(request, false, "Generate Merchant", afterEstimateConfirmation).then(response => {
        skeletonRefs.value[localGenId]?.completeProgress();
        setTimeout(() => {
            removeActiveGeneration(localGenId);
            requestNpcHistory();
        }, 1000);

        // Track NPC creation in Clarity
        clarityService.trackEvent('npc_created');

        progressStore.setEventMessage(null)
        statusStore.resetLapsedTime();
        promptStore.clearNpc();
    }).catch(function (error) {
        removeActiveGeneration(localGenId);
        data.isLoading = false;
        data.shouldDisplayNpcGenerateForm = true

        if (error.response?.status === 429) {
            generationStore.fetchActiveTasks();
        }

        const message = error.response?.data?.message ?? "Something went wrong. Please try again.";
        toast.error(message);
    });
}

function requestEquipment(stashId) {
    console.log('requestEquipment: ', stashId)
    data.isLoading = true;
    const request = { stash_id: stashId }
    equipmentApi.getEquipmentStashById(request).then(response => {
        data.response.equipment = response.data.equipment;
        data.response.stash_id = response.data.stash_id;
        console.log('equipment: ', data.response.equipment)
        data.isLoading = false;
        subscribeToEvents();
    }).catch(function (error) {
        data.isLoading = false;
        data.response.equipment = null
        const message = error.response?.data?.message ?? "Something went wrong. Please try again.";
        toast.error(message);
    });
}

function generateEquipment(request, stashId = null) {
    function afterEstimateConfirmation() {
        data.isLoading = true;
        data.shouldDisplayAddEquipmentForm = false;
        window.scrollTo(0,document.body.scrollHeight);
    }

    // Append the stash id if it exists
    if (stashId !== null) {
        request = { ...request, stash_id: stashId }
    }

    equipmentApi.generateEquipmentStash(request, false, "Generate Equipment", afterEstimateConfirmation).then(response => {
        if (data.response.equipment == null || data.response.equipment.length === 0) {
            data.response.equipment = response.data.equipment;
        } else {
            response.data.equipment.forEach(item => {
                const itemExists = _.some(data.response.equipment, existingItem => existingItem.id === item.id);
                if (!itemExists) {
                    data.response.equipment.push(item);
                }
            });
        }
        data.response.stash_id = response.data.stash_id;
        data.isLoading = false;
        promptStore.clearEquipment();
        subscribeToEvents();
    }).catch(function (error) {
        data.isLoading = false;
        const message = error.response?.data?.message ?? "Something went wrong. Please try again.";
        toast.error(message);
    });
}

function requestEquipmentUpdate(item) {
    equipmentApi.updateEquipment(item, data.response.stash_id).then(response => {
        const updatedItem = response.data;
        data.response.equipment = _.map(data.response.equipment, item => {
            if (item.id === updatedItem.id) { return updatedItem; }
            return item;
        });
        data.selectedItem = null;
    }).catch(function (error) {
        data.isLoading = false;
        data.response.equipment = null
        const message = error.response?.data?.message ?? "Something went wrong. Please try again.";
        toast.error(message);
    });
}

function requestDestroyEquipment(item) {
    console.log('requestDestroyEquipment: ', item)
    data.selectedItem = null
    equipmentApi.deleteEquipment(item.id, data.response.stash_id).then(response => {
        data.response.equipment = _.filter(data.response.equipment, item => {
            return item.id !== response.data.equipment_id;
        });
    }).catch(function (error) {
        const message = error.response?.data?.message ?? "Something went wrong. Please try again.";
        toast.error(message);
    });
}

function requestChangeIllustration(event) {
    const illustration = event.illustration
    if (data.selectedImageType === 'npc') {
        requestCharacterChangeIllustration(illustration)
    } else if (data.selectedImageType === 'equipment') {
        requestEquipmentChangeIllustration(illustration)
    }
}

function requestCharacterChangeIllustration(illustration) {
    data.shouldDisplaySearchIllustrationModal = false
    data.selectedImage = null
    temporalityRemoveImagesFromNpc()
    const request = {
        npc_id: data.response.npc.id,
        illustration_id: illustration.id,
    }
    npcApi.patchNpcIllustration(request).then(response => {
        data.response.npc = response.data;
        data.isLoading = false;
        data.selectedImage = null
    }).catch(function (error) {
        data.isLoading = false;
        const message = error.response?.data?.message ?? "Something went wrong. Please try again.";
        toast.error(message);
    });
}

function requestEquipmentChangeIllustration(illustration) {
    console.log('requestEquipmentChangeIllustration: ', illustration)
    console.log('requestEquipmentChangeIllustration: ', data.selectedItem)
    console.log('requestEquipmentChangeIllustration: ', data.selectedImage)
    removeImageFromExistingItem(data.selectedItem.id)

    const request = {
        equipment_id: data.selectedItem.id,
        illustration_id: illustration.id,
        stash_id: data.response.stash_id,
    }

    data.shouldDisplaySearchIllustrationModal = false
    data.selectedImage = null
    data.selectedItem = null

    equipmentApi.patchEquipmentIllustration(request).then(response => {
        data.response.equipment = _.map(data.response.equipment, item => {
            if (item.id === response.data.id) {
                return response.data;
            }
            return item;
        });
        data.isLoading = false;
        data.selectedImage = null
    }).catch(function (error) {
        data.isLoading = false;
        const message = error.response?.data?.message ?? "Something went wrong. Please try again.";
        toast.error(message);
    });
}

function requestNpcIllustrationImage(npcId, style, influence) {
    const request = {
        npc_id: npcId,
        style: style,
        influence: influence === '' ? null : influence,
    }
    function afterEstimateConfirmation() {
        data.selectedImage = null
        data.selectedItem = null
        // Reset illustration_failed flag to show loading state
        if (data.response.npc?.metadata) {
            data.response.npc.metadata.illustration_failed = false;
        }
        temporalityRemoveImagesFromNpc()
    }

    npcApi.postRegenerateIllustration(request, false, "Regenerate Illustration", afterEstimateConfirmation).then(response => {
    }).catch(function (error) {
        const message = error.response?.data?.message ?? "Something went wrong. Please try again.";
        toast.error(message);
    });
}

function requestEquipmentIllustrationImage(equipmentId, style, influence) {
    const request = {
        equipment_id: equipmentId,
        style: style,
        influence: influence === '' ? null : influence,
        stash_id: data.response.stash_id,
    }
    function afterEstimateConfirmation() {
        data.selectedImage = null
        data.selectedItem = null
        removeImageFromExistingItem(equipmentId)
    }

    equipmentApi.postRegenerateIllustration(request, false, "Regenerate Illustration", afterEstimateConfirmation).then(response => {
    }).catch(function (error) {
        const message = error.response?.data?.message ?? "Something went wrong. Please try again.";
        toast.error(message);
    });
}

function regenerateImage(entityId, style, influence) {
    if (data.selectedImageType === 'npc') {
        requestNpcIllustrationImage(entityId, style, influence)
    } else if (data.selectedImageType === 'equipment') {
        console.log('recognized equipment', data.selectedItem)
        requestEquipmentIllustrationImage(data.selectedItem.id, style, influence)
    } else {
        // If no selectedImageType is set, default to NPC (main entity) regeneration
        // This happens when regenerating from the options menu rather than clicking an image
        requestNpcIllustrationImage(entityId, style, influence)
    }
}

function requestNpcUpdate(event) {
    const request = {
        npc_id: data.response.npc.id
    }
    request[event.key] = event.value
    if (event.index != null) {
        request.index = event.index
    }
    npcApi.updateNpc(request).then(response => {
        data.response.npc = response.data;
        data.isLoading = false;
    }).catch(function (error) {
        data.isLoading = false;
        const message = error.response?.data?.message ?? "Something went wrong. Please try again.";
        toast.error(message);
    });
}

function requestNpcHistory() {
    if (!userStore.isLoggedIn()) {
        return;
    }

    const request = { resultLimit: 5, rangeStart: 0 }
    npcApi.getUsersNpcHistory(request).then(response => {
        data.response.historyBooklet = response.data.booklet;
    }).catch(function (error) {
        const message = error.response?.data?.message ?? "Something went wrong. Please try again.";
        toast.error(message);
    });
}

function requestNpcModification($event) {
    function afterEstimateConfirmation() {
        data.isLoading = true;
        data.response.npc = null
        data.shouldDisplayModifyContentForm = false
        statusStore.resetLapsedTime();
        window.scrollTo(0, 0)
    }

    const request = { npc_id: $event.entityId, influence: $event.influence }
    npcApi.postRequestCreatureModification(request, false, "Modify Npc", afterEstimateConfirmation).then(response => {
        data.response.npc = response.data;
        data.isLoading = false;
    }).catch(function (error) {
        data.isLoading = false;
        data.response.npc = null
        const message = error.response?.data?.message ?? "Something went wrong. Please try again.";
        toast.error(message);
    });
}

async function subscribeToEvents() {
    const ably = await initializeAbly()
    if (ably == null) { return; }

    if (data.response.stash_id != null) {
        const newEquipmentChannelName = 'private:stash.' + data.response.stash_id;
        if (currentEquipmentChannel != null && currentEquipmentChannel.name !== newEquipmentChannelName) {
            console.log('Unsubscribing from channel: ', currentEquipmentChannel.name)
            await currentEquipmentChannel.unsubscribe('equipment-updated');
            await currentEquipmentChannel.unsubscribe('equipment-removed');
            await currentEquipmentChannel.detach();
            currentEquipmentChannel = null;
        }

        if (currentEquipmentChannel?.name !== newEquipmentChannelName) {
            currentEquipmentChannel = ably.channels.get(newEquipmentChannelName);
            console.log('Subscribing to channel: ', currentEquipmentChannel.name)
            await currentEquipmentChannel.subscribe('equipment-updated', (message) => {
                const updatedItem = message.data;
                console.log('Updated item: ', updatedItem)
                data.response.equipment = _.map(data.response.equipment, item => {
                    if (item.id === updatedItem.id) {
                        return updatedItem;
                    }
                    return item;
                });

                // Check if updatedItem exists in the list. If it doesn't exist, append it to the list
                const updatedItemExists = _.some(data.response.equipment, item => item.id === updatedItem.id);
                if (!updatedItemExists) {
                    console.log('Appending item: ', updatedItem)
                    data.response.equipment.push(updatedItem);
                }
            });

            await currentEquipmentChannel.subscribe('equipment-removed', (message) => {
                const equipmentId = message.data.equipment_id;
                console.log('Removed item: ', equipmentId)
                data.response.equipment = _.filter(data.response.equipment, item => {
                    return item.id !== equipmentId;
                });
            });
        }
    }

    // Unsubscribe if already subscribed to a difference channel.
    const newNpcChannelName = 'private:npc.' + data.response.npc.id;
    if (currentNpcChannel != null && currentNpcChannel.name !== newNpcChannelName) {
        console.log('Unsubscribing from channel: ', currentNpcChannel.name)
        await currentNpcChannel.unsubscribe('npc-updated');
        await currentNpcChannel.detach();
    }
    if (currentNpcChannel?.name !== newNpcChannelName) {
        currentNpcChannel = ably.channels.get(newNpcChannelName);
        console.log('Subscribing to channel: ', newNpcChannelName)
        await currentNpcChannel.subscribe('npc-updated', (message) => {
            console.log('Updated npc: ', message)
            const updatedNpc = message.data;
            data.response.npc = updatedNpc
        });
    }
}

const shouldDisplayNoContent = computed(() => {
    return data.response.npc == null
})

const shouldDisplayWares = computed(() => {
    return data.response.equipment != null && data.response.equipment.length > 0
})

onMounted(() => {
    window.scrollTo(0, 0)
    statusStore.updateThrottling();

    const hasGenerateHash = location.hash === '#generate';
    if (hasGenerateHash) {
        data.shouldDisplayNpcGenerateForm = true;

        // Read edition from URL query parameter
        const edition = route.query.edition;
        if (edition && typeof edition === 'string') {
            promptStore.npc.edition = edition;
        } else {
            // Guard against undefined - default to 5e
            promptStore.npc.edition = '5e';
        }
    }

    restoreActiveNpcGeneration();
})

function restoreActiveNpcGeneration() {
    if (!userStore.isLoggedIn()) return;
    if (route.query.npc_id) return;

    generationStore.initialize(userStore.userId);

    generationStore.fetchActiveTasks().then(() => {
        const npcTasks = generationStore.getActiveTasksByType('npc');
        if (npcTasks.length > 0) {
            data.isLoading = false;
            data.response.npc = null;

            npcTasks.forEach(task => {
                activeGenerations.value.unshift({
                    taskId: task.id,
                    influence: task.metadata?.influence || '',
                    edition: task.metadata?.edition || '5e',
                });
            });

            if (!data.response.historyBooklet) {
                requestNpcHistory();
            }

            setTimeout(() => {
                npcTasks.forEach(task => {
                    const elapsedSeconds = (Date.now() - new Date(task.started_at).getTime()) / 1000;
                    skeletonRefs.value[task.id]?.resumeProgress(Math.max(0, elapsedSeconds));
                });
            }, 50);
        }
    }).catch(() => {});

    unsubscribeTaskCompleted = generationStore.onTaskCompleted((taskData) => {
        const matching = activeGenerations.value.find(g => g.taskId === taskData.id);
        if (!matching) return;

        if (taskData.status === 'completed' && taskData.entity_id) {
            skeletonRefs.value[taskData.id]?.completeProgress();
            setTimeout(() => {
                removeActiveGeneration(taskData.id);
                requestNpcHistory();
            }, 1000);
        } else {
            removeActiveGeneration(taskData.id);
            if (taskData.status === 'failed') {
                toast.error("Merchant generation failed. Please try again.");
            }
        }
    });
}

onUnmounted(() => {
    if (unsubscribeTaskCompleted) {
        unsubscribeTaskCompleted();
        unsubscribeTaskCompleted = null;
    }
})

watch(
    () => route.query.npc_id,
    async queryNpcId => {
        if (queryNpcId) {
            if (data.response.npc?.id === queryNpcId) {
                return;
            }
            requestMerchant(queryNpcId)
        } else {
            data.isLoading = false
            data.response.equipment = null
            data.response.npc = null
            requestNpcHistory()
        }
    },
    { immediate: true }
)

const progressMessage = computed(() => {
    return progressStore.getEventMessage()
})

function imageSelected(image, displayRegenerationLimit, type) {
    data.selectedImage = image;
    data.selectedImageType = type;
    data.displayRegenerationLimit = displayRegenerationLimit;
}

function removeImageFromExistingItem(uuid) {
    // Remove the image from the NPC (if it's the NPC's image)
    data.response.npc.illustrations = data.response.npc.illustrations.map(illustration => {
        if (illustration.id === uuid) {
            illustration.url = null;
        }
        return illustration;
    });

    // Remove the image from the equipment (if it's the equipment's image)
    data.response.equipment.forEach(equipmentItem => {
        equipmentItem.illustrations = equipmentItem.illustrations.map(illustration => {
            if (illustration.id === uuid) {
                illustration.url = null;
            }
            return illustration;
        });
    });

    data.response.equipment.forEach(equipmentItem => {
        if (equipmentItem.id === uuid) {
            equipmentItem.illustrations = [];
        }
    });
}

function temporalityRemoveImagesFromNpc(uuid) {
    data.response.npc.illustrations = []
}

function toggleEdit() {
    data.isEditing = !data.isEditing;
}

const isNpcAuthor = computed(() => {
    return data.response?.npc?.user_id === userStore.getUserIdFromToken();
})

function maybeDisplayMerchantGenerationForm() {
    if (userStore.isLoggedIn() && creditStore.currentBalance <= 0 && creditStore.unlimited === false) {
        data.shouldDisplayCreditPlans = true;
        return
    }

    data.shouldDisplayNpcGenerateForm = true;
}

function maybeDisplayAddEquipmentGenerationForm() {
    if (userStore.isLoggedIn() && creditStore.currentBalance <= 0 && creditStore.unlimited === false) {
        data.shouldDisplayCreditPlans = true;
        return
    }

    if (!userStore.isLoggedIn()) {
        data.displayLogin = true;
        data.authenticationCallback = () => {
            data.displayLogin = false;
            data.displayRegister = false;
            data.shouldDisplayAddEquipmentForm = true;
        }
    } else {
        data.shouldDisplayAddEquipmentForm = true;
    }
}

function isEquipmentAuthor(item) {
    return item.user_id === userStore.getUserIdFromToken();
}

const isLoggedIn = computed(() => {
    return !!userStore.userId
})

const shouldDisplayAddEquipmentButton = computed(() => {
    if (!data.response.equipment) {
        return false;
    }
    if (!isEquipmentAuthor(data.response.equipment[0])) {
        return false;
    }
    return !!(data.response.stash_id && data.response.equipment.length < 10);
});

const shouldDisplayRecentMerchants = computed(() => {
    if (isGenerating.value) {
        return true;
    }
    if (!data.isLoading && data.response.npc == null && data.response.historyBooklet?.npcs.length > 0) {
        return true;
    }
    return false
})


const shouldDisplayRegenerateIllustrationOption = computed(() => {
    return !!isNpcAuthor
})

</script>

<template>
    <title>Tavern of Azoth - Merchants</title>
    <meta name="description" content="Merchants - Generate merchants contextual to your campaign. Merchants come with an introduction dialog and a backstory. Their equipment helps eliminate choice fatigue so you can focus on your journey."/>
    <PrimaryLayout>
        <div class="bg-gray-900">
            <div class="wrapper">
                <LoginModal
                    v-if="data.displayLogin"
                    v-show="data.displayLogin"
                    @close="data.displayLogin = false"
                    @register="data.displayRegister = true"
                    @login-success="data.authenticationCallback"
                />
                <RegistrationModal
                    v-if="data.displayRegister"
                    v-show="data.displayRegister"
                    @close="data.displayRegister = false"
                    @login="data.displayLogin = true"
                    @registration-success="data.authenticationCallback"
                />
                <EquipmentPreview v-if="data.selectedItem"
                    :item="data.selectedItem"
                    @updated="requestEquipmentUpdate($event)"
                    @destroy-item="requestDestroyEquipment($event)"
                    @close="data.selectedItem = null"
                    @image-selected="imageSelected($event, false, 'equipment')"
                />
                <EquipmentStashGenerateForm v-if="data.shouldDisplayAddEquipmentForm"
                    title="Add Equipment"
                    @close="data.shouldDisplayAddEquipmentForm = null"
                    @generate-stash="generateEquipment($event, data.response.stash_id)"
                />
                <NpcGenerateForm v-if="data.shouldDisplayNpcGenerateForm"
                    @close="data.shouldDisplayNpcGenerateForm = null"
                    @generate-npc="generateMerchant($event)"
                />
                <MiniPreview v-if="data.selectedImage"
                    :imageUrl="data.selectedImage"
                    :is-author="isNpcAuthor"
                    :regeneration-count="data.response.npc.image_regenerations"
                    :has-regeneration-limit="data.displayRegenerationLimit"
                    :buttonOptions="{ displayUploadImageButton: true, displaySelectImageButton: true, displayRegenerateButton: true, displayInpaintButton: true }"
                    :using-illustration-api="true"
                    @regenerate-image="data.shouldDisplaySelectStyleForm = true"
                    @change-image="data.shouldDisplaySearchIllustrationModal = true"
                    @close="data.selectedImage = null"
                />
                <BookletContentForm v-if="data.shouldDisplayAddBookletContentForm"
                    title="Add Merchant to Collection"
                    :npc-id="data.response.npc?.id"
                    :is-author="isNpcAuthor"
                    @close="data.shouldDisplayAddBookletContentForm = null"
                />
                <SelectStyleForm v-if="data.shouldDisplaySelectStyleForm"
                    :entity-id="data.response.npc.id"
                    content-type="npc"
                    :default-style="data.response.npc.style"
                    :regeneration-count="data.response.npc?.image_regenerations"
                    :default-description="data.response.npc?.image_description"
                    :illustration-failed="data.response.npc?.metadata?.illustration_failed"
                    @regenerate="regenerateImage($event.entityId, $event.style, $event.influence)"
                    @close="data.shouldDisplaySelectStyleForm = false"
                />
                <MetadataPreview v-if="data.shouldDisplayMetadataPreview"
                    content-type="npc"
                    :metadata="data.response.npc.metadata"
                    @close="data.shouldDisplayMetadataPreview = false"
                />
                <ModifyContentForm v-if="data.shouldDisplayModifyContentForm"
                    :entity-id="data.response.npc.id"
                    @modify="requestNpcModification($event)"
                    @close="data.shouldDisplayModifyContentForm = false"
                />
                <CreditsExhaustedModal v-if="data.shouldDisplayCreditPlans"
                    :show="data.shouldDisplayCreditPlans"
                    @close="data.shouldDisplayCreditPlans = false"
                />
                <SearchIllustrationsModal v-if="data.shouldDisplaySearchIllustrationModal"
                    :entity-id="data.selectedImageType === 'npc' ? data.response.npc.id : data.selectedItem.id"
                    :illustration-types="data.selectedImageType === 'npc' ? ['character', 'npc', 'creature'] : ['equipment']"
                    :show="data.shouldDisplaySearchIllustrationModal"
                    @close="data.shouldDisplaySearchIllustrationModal = false"
                    @illustration-selected="requestChangeIllustration($event)"
                />
                <SubscriptionPricingModal
                    :show="data.showConcurrencyPricingModal"
                    highlight-feature="simultaneous-generations"
                    title="Upgrade for more simultaneous generations"
                    @close="data.showConcurrencyPricingModal = false"
                />
                <div style="display: flex; justify-content: space-evenly; flex-wrap: wrap">
                    <div class="max-w-7xl w-full mt-6" style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap">
                        <ReturnToListButton v-if="showBackButton" label="Back to Merchants" to="/merchant" class="ml-4 sm:ml-8" />
                        <div style="margin-left: auto">
                            <div style="display: flex; justify-content: flex-end; gap: 10px;" v-if="!shouldDisplayNoContent">
                                <FontAwesomeIcon v-if="isNpcAuthor" size="2x" :icon="['fad', 'pen-to-square']" :style="data.isEditing ? '--fa-secondary-color: #3b82f6' : '--fa-secondary-color: #1e3a8a'" style="--fa-primary-color: #1d4ed8; cursor: pointer; margin-top: 9px" @click="toggleEdit"/>
                                <OptionsMenu
                                    :show-collection="isLoggedIn" :callback-collection="() => { data.shouldDisplayAddBookletContentForm = true }"
                                    :show-metadata="!!(data.response.npc.metadata)" :callback-metadata="() => { data.shouldDisplayMetadataPreview = true }"
                                    :show-regenerate-image="shouldDisplayRegenerateIllustrationOption" :callback-regenerate-image="() => { data.shouldDisplaySelectStyleForm = true }"
                                    :show-modify-content="isNpcAuthor" :callback-modify-content="() => { data.shouldDisplayModifyContentForm = true }"
                                />
                                <div class="fa-stack right-2 mr-2 -ml-1 w-8" @click="maybeDisplayAddEquipmentGenerationForm" style="transform: scale(0.45); margin-left: auto">
                                    <font-awesome-icon :icon="['far', 'sword']" size="2x" class="mt-2 ml-2 fa-stack-2x" style="color: #1d4ed8; cursor: pointer" />
                                    <font-awesome-icon :icon="['fas', 'plus']" class="fa-stack-2x" style="color: #1d4ed8; cursor: pointer" />
                                </div>
                                <Lottie :loop="true" :animationData="ChemistryFlask" style="cursor: pointer; width: 40px; margin-right: 1.3rem;" @click="maybeDisplayMerchantGenerationForm"/>
                            </div>
                        </div>
                    </div>
                </div>
                <div style="display: flex; justify-content: space-evenly; flex-wrap: wrap">
                    <div class="max-w-7xl w-full mb-6">
                        <MerchantInfo v-if="data.response.npc"
                            :npc="data.response.npc"
                            :is-author="isNpcAuthor"
                            :is-editing="data.isEditing"
                            @image-selected="imageSelected($event, true, 'npc')"
                            @npc-updated="requestNpcUpdate($event)"
                            @display-regenerate-illustration="data.shouldDisplaySelectStyleForm = true"
                        />
                        <div class="mr-0 ml-0 sm:mr-8 sm:ml-8">
                            <div class="mr-4  flex space-x-2" v-if="!shouldDisplayNoContent && !data.isLoading && shouldDisplayWares">
                                <h1 class="text-white text-2xl p-4">Wares</h1>
                                <div class="fa-stack fa-lg mr-2 -ml-1" v-if="shouldDisplayAddEquipmentButton" @click="maybeDisplayAddEquipmentGenerationForm" style="transform: scale(0.45); margin-left: auto">
                                    <font-awesome-icon :icon="['far', 'sword']" size="2x" class="mt-2 ml-2 fa-stack-2x" style="color: #1d4ed8; cursor: pointer" />
                                    <font-awesome-icon :icon="['fas', 'plus']" class="fa-stack-2x" style="color: #1d4ed8; cursor: pointer" />
                                </div>
                            </div>
                            <EquipmentList v-if="data.response.equipment && !data.isLoading"
                                :equipment="data.response.equipment"
                                :stash-id="data.response.stash_id"
                                @image-selected="imageSelected($event, false, 'equipment')"
                                @item-selected="data.selectedItem = $event"
                            />
                        </div>
                        <Lottie v-if="data.isLoading && !isGenerating" :loop="true" :animationData="LoadingMotion" style="pointer-events: none; width: 200px; margin: 0 auto;"/>
                        <div v-if="data.isLoading && !isGenerating && progressMessage" class="flex justify-center items-center">
                            <p class="w-1/2  text-sm text-white text-center">{{progressMessage}}</p>
                        </div>
                    </div>
                </div>
                <div class="flex justify-center ">
                    <GenerateActionPanel v-if="shouldDisplayNoContent && !data.isLoading"
                        class="max-w-2xl"
                        title="Merchants"
                        :icon="['fad', 'sack']"
                        description="Utilize our <b class='text-white'>text-to-merchant</b> service to design distinctive merchants, each equipped with unique items relevant to your campaign's context."
                        @generate="maybeDisplayMerchantGenerationForm"
                    />
                </div>
                <div v-if="shouldDisplayRecentMerchants" class="flex items-center" :class="isGenerating ? 'mt-6' : 'mt-12'" >
                    <div class="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-1 gap-4 px-0 sm:px-4 md:px-0 mb-8">
                        <div class="max-w-2xl">
                            <p class="text-2xl text-white mt-2 mb-2 sm:px-0 px-2">Your Recent Merchants</p>
                            <div v-if="isGenerating" class="overflow-hidden bg-gray-800 shadow sm:rounded-md" :class="{ 'mb-4': data.response.historyBooklet?.npcs?.length > 0 }">
                                <ul role="list">
                                    <NpcSkeletonCard
                                        v-for="gen in activeGenerations"
                                        :key="gen.taskId"
                                        :ref="el => { if (el) skeletonRefs[gen.taskId] = el; else delete skeletonRefs[gen.taskId]; }"
                                        :influence="gen.influence"
                                        :edition="gen.edition"
                                    />
                                </ul>
                            </div>
                            <NpcList v-if="data.response.historyBooklet?.npcs?.length > 0"
                                :npcs="data.response.historyBooklet.npcs"
                                :pagination="null"
                                :display-view-all-button="true"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </PrimaryLayout>
</template>

<style scoped>
.wrapper {
    min-height: 100vh;
    margin: 0 auto 0; /* the bottom margin is the negative value of the footer's height */
}
.fade {
    opacity: 1;
    -webkit-transition: opacity 500ms linear;
    transition: opacity 500ms linear;
}
</style>
